import { defineCopy } from '../pageCopy';

/**
 * Copy for the portfolio index page (/portfolio, /ar/portfolio, /ckb/portfolio).
 * Category labels reuse the exact terms already used in the project data
 * translations (src/lib/data.ts) so the filters match the card categories.
 */
const en = {
  meta: {
    title: 'Our Projects',
    description:
      'Selected work from Datacode: web apps, AI integrations, and mobile products built for clients in Kurdistan and worldwide. Each project is a real, shipped product.',
    ogTitle: 'Our Projects | Datacode',
    ogDescription:
      'Selected work: web apps, AI integrations, and mobile products, each a real shipped product.',
  },
  hero: {
    eyebrow: 'Selected work, built in Erbil',
    title: 'PROJECTS',
    scrollToExplore: 'Scroll to Explore',
  },
  filters: {
    all: 'All',
    mobileApp: 'Mobile App',
    webDesign: 'Web Design',
  },
  grid: {
    loading: 'Loading projects...',
    empty: 'No projects available yet.',
    getInTouch: 'Get In Touch',
    viewLabel: 'View',
  },
  // Used only for projects whose data.ts entry has no ar/ckb translation yet.
  // Keyed by the exact English category/title.
  fallbacks: {
    categories: {
      'Mobile App': 'Mobile App',
      'Web Design': 'Web Design',
      'AI Integration': 'AI Integration',
      'Cross-border Commerce': 'Cross-border Commerce',
      'Hospitality ERP': 'Hospitality ERP',
      'Enterprise Software': 'Enterprise Software',
      'SaaS Platform': 'SaaS Platform',
      'Web Platform': 'Web Platform',
      'Marketplace Platform': 'Marketplace Platform',
      'E-commerce': 'E-commerce',
      Healthcare: 'Healthcare',
      'Logistics Platform': 'Logistics Platform',
    } as Record<string, string>,
    titles: {
      'Hotel Management': 'Hotel Management',
      'Multi-site Oil Production System': 'Multi-site Oil Production System',
      'DC-RMS Restaurant Management': 'DC-RMS Restaurant Management',
      'Kurdish Calendar': 'Kurdish Calendar',
      'Smart Community Platform': 'Smart Community Platform',
    } as Record<string, string>,
  },
};

const ar: typeof en = {
  meta: {
    title: 'مشاريعنا',
    description:
      'أعمال مختارة من Datacode: تطبيقات ويب، ودمج الذكاء الاصطناعي، ومنتجات موبايل بنيناها لعملاء في كوردستان وحول العالم. كل مشروع منتج حقيقي انطلق فعلاً.',
    ogTitle: 'مشاريعنا | Datacode',
    ogDescription:
      'أعمال مختارة: تطبيقات ويب، ودمج الذكاء الاصطناعي، ومنتجات موبايل، وكل واحد منها منتج حقيقي انطلق فعلاً.',
  },
  hero: {
    eyebrow: 'أعمال مختارة، بُنيت في أربيل',
    title: 'المشاريع',
    scrollToExplore: 'مرر للأسفل للاستكشاف',
  },
  filters: {
    all: 'الكل',
    mobileApp: 'تطبيق موبايل',
    webDesign: 'تصميم مواقع',
  },
  grid: {
    loading: 'جاري تحميل المشاريع...',
    empty: 'لا توجد مشاريع متاحة حالياً.',
    getInTouch: 'تواصل معنا',
    viewLabel: 'عرض',
  },
  fallbacks: {
    categories: {
      'Mobile App': 'تطبيق موبايل',
      'Web Design': 'تصميم مواقع',
      'AI Integration': 'دمج الذكاء الاصطناعي',
      'Cross-border Commerce': 'تجارة عابرة للحدود',
      'Hospitality ERP': 'نظام ERP للضيافة',
      'Enterprise Software': 'برمجيات مؤسسات',
      'SaaS Platform': 'منصة SaaS',
      'Web Platform': 'منصة ويب',
      'Marketplace Platform': 'منصة سوق إلكتروني',
      'E-commerce': 'تجارة إلكترونية',
      Healthcare: 'رعاية صحية',
      'Logistics Platform': 'منصة لوجستية',
    },
    titles: {
      'Hotel Management': 'إدارة الفنادق',
      'Multi-site Oil Production System': 'نظام إنتاج نفط متعدد المواقع',
      'DC-RMS Restaurant Management': 'DC-RMS لإدارة المطاعم',
      'Kurdish Calendar': 'التقويم الكوردي',
      'Smart Community Platform': 'منصة المجتمع الذكي',
    },
  },
};

const ckb: typeof en = {
  meta: {
    title: 'پرۆژەکانمان',
    description:
      'کارە هەڵبژاردەکانی Datacode: ئەپی وێب، تێکەڵکردنی زیرەکی دەستکرد و بەرهەمی مۆبایل کە بۆ کڕیاران لە کوردستان و سەرانسەری جیهان دروستمان کردوون. هەر پرۆژەیەک بەرهەمێکی ڕاستەقینەیە و بڵاو کراوەتەوە.',
    ogTitle: 'پرۆژەکانمان | Datacode',
    ogDescription:
      'کارە هەڵبژاردەکان: ئەپی وێب، تێکەڵکردنی زیرەکی دەستکرد و بەرهەمی مۆبایل، هەر یەکێکیان بەرهەمێکی ڕاستەقینەی بڵاوکراوەیە.',
  },
  hero: {
    eyebrow: 'کارە هەڵبژاردەکان، لە هەولێر دروست کراون',
    title: 'پرۆژەکان',
    scrollToExplore: 'بۆ گەڕان بڕۆ خوارەوە',
  },
  filters: {
    all: 'هەموو',
    mobileApp: 'ئەپی مۆبایل',
    webDesign: 'دیزاینی وێب',
  },
  grid: {
    loading: 'پرۆژەکان بار دەکرێن...',
    empty: 'هێشتا هیچ پرۆژەیەک نییە.',
    getInTouch: 'پەیوەندیمان پێوە بکە',
    viewLabel: 'بینین',
  },
  fallbacks: {
    categories: {
      'Mobile App': 'ئەپی مۆبایل',
      'Web Design': 'دیزاینی وێب',
      'AI Integration': 'تێکەڵکردنی زیرەکی دەستکرد',
      'Cross-border Commerce': 'بازرگانی دەرەوەی سنوور',
      'Hospitality ERP': 'سیستەمی ERP بۆ میوانداری',
      'Enterprise Software': 'نەرمەکاڵای دامەزراوەیی',
      'SaaS Platform': 'پلاتفۆرمی SaaS',
      'Web Platform': 'پلاتفۆرمی وێب',
      'Marketplace Platform': 'پلاتفۆرمی بازاڕ',
      'E-commerce': 'بازرگانی ئەلیکترۆنی',
      Healthcare: 'چاودێری تەندروستی',
      'Logistics Platform': 'پلاتفۆرمی لۆجستی',
    },
    titles: {
      'Hotel Management': 'بەڕێوەبردنی هوتێل',
      'Multi-site Oil Production System': 'سیستەمی بەرهەمهێنانی نەوت لە چەند شوێنێک',
      'DC-RMS Restaurant Management': 'DC-RMS بۆ بەڕێوەبردنی چێشتخانە',
      'Kurdish Calendar': 'ڕۆژژمێری کوردی',
      'Smart Community Platform': 'پلاتفۆرمی کۆمەڵگای زیرەک',
    },
  },
};

export const portfolioCopy = defineCopy(en, ar, ckb);
